import { useAtom } from 'jotai';
import { hoveredViewAreaAtom } from '../states/planner';
import { storageUrl } from '../firebase';
import { useCallback, useEffect, useRef, useState } from 'react';
import useViews from '../hooks/useViews';
import { getAnchor } from './CustomShapeArea';

export const AreaViewHover = () => {
  const [hoveredArea] = useAtom(hoveredViewAreaAtom);
  const [tooltipSize, setTooltipSize] = useState({ width: 0, height: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [views] = useViews();

  const updateSize = useCallback(() => {
    if (tooltipRef.current?.parentElement) {
      const parent = tooltipRef.current.parentElement;
      const { offsetWidth, offsetHeight } = tooltipRef.current;

      setTooltipSize({
        width: (offsetWidth / parent.offsetWidth) * 100,
        height: (offsetHeight / parent.offsetHeight) * 100,
      });
    }
  }, []);

  useEffect(() => {
    if (!tooltipRef.current?.parentElement) return;
    const parent = tooltipRef.current.parentElement;

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(parent);

    return () => observer.disconnect();
  }, [hoveredArea, updateSize]);

  if (!hoveredArea) return null;

  const svgArea = hoveredArea;

  // const view = views.find(v => v.viewid === svgArea.reference_id);
  let view = null;
  if (views && svgArea.reference_id) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    view = views[Number(svgArea.reference_id)];
  }

  if (!view) return null;

  const directionToString = (d: string) => {
    if (d === 'S' || d === 'SSE' || d === 'SSW') return 'Nord';
    if (d === 'E' || d === 'ENE' || d === 'ESE') return 'Väst';
    if (d === 'W' || d === 'WNW' || d === 'WSW') return 'Öst';
    if (d === 'N' || d === 'NNE' || d === 'NNW') return 'Syd';
    if (d === 'SE') return 'NordVäst';
    if (d === 'SW') return 'NordOst';
    if (d === 'NE' || d === 'NNE') return 'SydVäst';
    if (d === 'NW' || d === '') return 'SydOst';
  };

  const tooltipplace = svgArea.tooltipplace;
  const anchor = getAnchor(tooltipplace, svgArea, tooltipSize);

  return (
    <div
      ref={tooltipRef}
      className={`pointer-events-none absolute transform rounded-md shadow-lg transition-all duration-300 ease-out ${
        hoveredArea ? 'visible scale-100' : 'invisible scale-75'
      }`}
      style={{
        top: `${anchor.y}%`,
        left: `${anchor.x}%`,
        maxWidth: '25%',
        width: 'auto',
      }}
    >
      {tooltipplace === 'top' && (
        <div className="absolute left-1/2 top-full -translate-x-1/2 border-x-[7px] border-t-[7px] border-x-transparent border-t-white opacity-95" />
      )}
      {tooltipplace === 'bottom' && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-x-[7px] border-b-[7px] border-x-transparent border-b-white opacity-95" />
      )}
      {tooltipplace === 'left' && (
        <div className="absolute left-full top-1/2 -translate-y-1/2 border-y-[7px] border-l-[7px] border-y-transparent border-l-white opacity-95" />
      )}
      {tooltipplace === 'right' && (
        <div className="absolute right-full top-1/2 -translate-y-1/2 border-y-[7px] border-r-[7px] border-y-transparent border-r-white opacity-95" />
      )}
      <div>
        <img
          className="h-auto max-w-full rounded-t-md border-2"
          src={storageUrl(view.daypicture)}
          alt="floorplanpicture"
          onLoad={updateSize}
        />
      </div>
      <div className="h-10 overflow-hidden text-ellipsis whitespace-nowrap rounded-b-md bg-white px-2 pb-2 pt-2 text-center text-[clamp(0.75rem,2vw,1rem)] opacity-95">
        Kameravy mot {directionToString(view.compass)}
      </div>
    </div>
  );
};
